// FAZ-10.6 / K-34 + K-39 — genişletilmiş atomik blok View'ları (14 yeni blok).
// Tümü saf React (Puck'sız), JS'siz (K-2 korunur), opsiyonel yapılandırılmış
// styleJson taşır (K-31/K-40; `resolveBlockStyle` + `useHeadStyle`). blockViews.tsx'teki
// çekirdek 8 blokla AYNI stil/güvenlik desenini paylaşır.
//
// GÜVENLİK: HTML alanları (alert/accordion/htmlEmbed) `sanitizeHtml` (DOMPurify,
// BE Ganss paritesi) ile temizlenir; URL alanları `isSafeUrl` geçidinden geçer;
// video embed iframe'i HAM URL'den değil `parseVideoEmbed` doğrulanmış id'sinden
// kurulur; formRef/sliderRef/mapRef admin önizlemede hafif placeholder-kart olur.

import type { CSSProperties, ReactNode } from 'react';
import { sanitizeHtml } from '@shared/utils/sanitize';
import { isSafeUrl } from '@shared/utils/safeUrl';
import { resolveAssetUrl } from '@shared/utils/assetUrl';
import { resolveBlockStyle } from './blockStyle';
import { useHeadStyle } from './useHeadStyle';
import { cx } from './cx';
import { ICON_COMPONENTS } from './icons';
import { parseVideoEmbed } from './videoEmbed';
import {
  clampMapZoom,
  type AlertTone,
  type BadgeTone,
  type IconName,
  type IconTone,
  type SocialLinkItem,
  type SocialSize,
  type VideoSource,
} from './studioBlocks';

function safeUrlOrUndefined(url: string | undefined | null): string | undefined {
  return url && isSafeUrl(url) ? url : undefined;
}

// --- Ayraç ---

export function DividerView({ styleJson }: { styleJson?: string }) {
  const rs = resolveBlockStyle(styleJson);
  useHeadStyle(rs.scopedClass, rs.css);
  return (
    <hr
      className={cx('cs-block cs-block--divider', rs.scopedClass)}
      style={{
        border: 'none',
        borderTop: '1px solid var(--ds-border, #cbd5e1)',
        margin: '16px 0',
        ...rs.style,
      }}
    />
  );
}

// --- Alıntı ---

export function QuoteView({
  text,
  author,
  styleJson,
}: {
  text: string;
  author?: string;
  styleJson?: string;
}) {
  const rs = resolveBlockStyle(styleJson);
  useHeadStyle(rs.scopedClass, rs.css);
  return (
    <blockquote
      className={cx('cs-block cs-block--quote', rs.scopedClass)}
      style={{
        margin: 0,
        padding: '8px 0 8px 16px',
        borderLeft: '3px solid var(--ds-brand, #0a7d55)',
        fontStyle: 'italic',
        color: 'var(--ds-text-primary, inherit)',
        ...rs.style,
      }}
    >
      <p style={{ margin: 0 }}>{text || '—'}</p>
      {author ? (
        <cite
          style={{
            display: 'block',
            marginTop: 6,
            fontSize: 13,
            fontStyle: 'normal',
            color: 'var(--ds-text-secondary, #64748b)',
          }}
        >
          — {author}
        </cite>
      ) : null}
    </blockquote>
  );
}

// --- Bilgi Kutusu ---

const ALERT_TONE_STYLE: Record<AlertTone, CSSProperties> = {
  info: { background: 'var(--ds-info-bg, #eff6ff)', color: 'var(--ds-info-fg, #1e40af)', borderColor: 'var(--ds-info-border, #bfdbfe)' },
  success: { background: 'var(--ds-success-bg, #f0fdf4)', color: 'var(--ds-success-fg, #166534)', borderColor: 'var(--ds-success-border, #bbf7d0)' },
  warning: { background: 'var(--ds-warning-bg, #fffbeb)', color: 'var(--ds-warning-fg, #92400e)', borderColor: 'var(--ds-warning-border, #fde68a)' },
  error: { background: 'var(--ds-error-bg, #fef2f2)', color: 'var(--ds-error-fg, #991b1b)', borderColor: 'var(--ds-error-border, #fecaca)' },
};

export function AlertView({
  tone,
  html,
  styleJson,
}: {
  tone?: AlertTone;
  html: string;
  styleJson?: string;
}) {
  const rs = resolveBlockStyle(styleJson);
  useHeadStyle(rs.scopedClass, rs.css);
  return (
    <div
      className={cx('cs-block cs-block--alert', rs.scopedClass)}
      style={{
        padding: '12px 16px',
        borderRadius: 8,
        border: '1px solid',
        ...ALERT_TONE_STYLE[tone ?? 'info'],
        ...rs.style,
      }}
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
    />
  );
}

// --- Rozet ---

const BADGE_TONE_STYLE: Record<BadgeTone, CSSProperties> = {
  default: { background: 'var(--ds-surface-2, #eef2f7)', color: 'var(--ds-text-secondary, #475569)' },
  brand: { background: 'var(--ds-brand, #0a7d55)', color: '#fff' },
  success: { background: 'var(--ds-success-bg, #dcfce7)', color: 'var(--ds-success-fg, #166534)' },
  warning: { background: 'var(--ds-warning-bg, #fef3c7)', color: 'var(--ds-warning-fg, #92400e)' },
};

export function BadgeView({
  label,
  tone,
  styleJson,
}: {
  label: string;
  tone?: BadgeTone;
  styleJson?: string;
}) {
  const rs = resolveBlockStyle(styleJson);
  useHeadStyle(rs.scopedClass, rs.css);
  return (
    <span
      className={cx('cs-block cs-block--badge', rs.scopedClass)}
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.6,
        ...BADGE_TONE_STYLE[tone ?? 'default'],
        ...rs.style,
      }}
    >
      {label || '—'}
    </span>
  );
}

// --- İstatistik ---

export function StatView({
  value,
  label,
  styleJson,
}: {
  value: string;
  label: string;
  styleJson?: string;
}) {
  const rs = resolveBlockStyle(styleJson);
  useHeadStyle(rs.scopedClass, rs.css);
  return (
    <div
      className={cx('cs-block cs-block--stat', rs.scopedClass)}
      style={{ display: 'flex', flexDirection: 'column', gap: 2, ...rs.style }}
    >
      <span style={{ fontSize: 32, fontWeight: 700, lineHeight: 1.1, color: 'var(--ds-brand, #0a7d55)' }}>
        {value || '—'}
      </span>
      <span style={{ fontSize: 14, color: 'var(--ds-text-secondary, #64748b)' }}>{label || '—'}</span>
    </div>
  );
}

// --- Akordeon (JS'siz: <details>/<summary>) ---

export function AccordionView({
  title,
  html,
  styleJson,
}: {
  title: string;
  html: string;
  styleJson?: string;
}) {
  const rs = resolveBlockStyle(styleJson);
  useHeadStyle(rs.scopedClass, rs.css);
  return (
    <details
      className={cx('cs-block cs-block--accordion', rs.scopedClass)}
      style={{ border: '1px solid var(--ds-border, #e2e8f0)', borderRadius: 8, padding: '8px 12px', ...rs.style }}
    >
      <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{title || '—'}</summary>
      <div style={{ marginTop: 8 }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }} />
    </details>
  );
}

// --- Liste ---

export function ListView({
  items,
  ordered,
  styleJson,
}: {
  items: string[];
  ordered?: boolean;
  styleJson?: string;
}) {
  const rs = resolveBlockStyle(styleJson);
  useHeadStyle(rs.scopedClass, rs.css);
  const Tag = ordered ? 'ol' : 'ul';
  const list = items.length > 0 ? items : ['—'];
  return (
    <Tag
      className={cx('cs-block cs-block--list', rs.scopedClass)}
      style={{ margin: 0, paddingInlineStart: 22, ...rs.style }}
    >
      {list.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </Tag>
  );
}

// --- Video (embed = doğrulanmış iframe; file = <video controls>) ---

export function VideoView({
  source,
  url,
  styleJson,
}: {
  source: VideoSource;
  url: string;
  styleJson?: string;
}) {
  const rs = resolveBlockStyle(styleJson);
  useHeadStyle(rs.scopedClass, rs.css);
  const wrapperStyle: CSSProperties = { position: 'relative', width: '100%', ...rs.style };

  let inner: ReactNode;
  if (source === 'file') {
    const safe = safeUrlOrUndefined(url);
    inner = safe ? (
      <video controls style={{ width: '100%', display: 'block', borderRadius: 8 }} src={resolveAssetUrl(safe)}>
        <track kind="captions" />
      </video>
    ) : (
      <VideoPlaceholder />
    );
  } else {
    const embed = parseVideoEmbed(url);
    inner = embed ? (
      <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
        <iframe
          src={embed.embedUrl}
          title="video"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none', borderRadius: 8 }}
        />
      </div>
    ) : (
      <VideoPlaceholder />
    );
  }

  return (
    <div className={cx('cs-block cs-block--video', rs.scopedClass)} style={wrapperStyle}>
      {inner}
    </div>
  );
}

function VideoPlaceholder() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        aspectRatio: '16 / 9',
        borderRadius: 8,
        border: '1px dashed var(--ds-border, #cbd5e1)',
        background: 'var(--ds-surface-2, #f8fafc)',
        color: 'var(--ds-text-secondary, #64748b)',
        fontSize: 13,
      }}
    >
      Video
    </div>
  );
}

// --- İkon (curated lucide) ---

const ICON_TONE_COLOR: Record<IconTone, string | undefined> = {
  default: undefined,
  brand: 'var(--ds-brand, #0a7d55)',
  muted: 'var(--ds-text-secondary, #64748b)',
};

export function IconView({
  name,
  size,
  tone,
  styleJson,
}: {
  name: IconName;
  size?: number;
  tone?: IconTone;
  styleJson?: string;
}) {
  const rs = resolveBlockStyle(styleJson);
  useHeadStyle(rs.scopedClass, rs.css);
  const IconCmp = ICON_COMPONENTS[name];
  const color = tone ? ICON_TONE_COLOR[tone] : undefined;
  return (
    <span className={cx('cs-block cs-block--icon', rs.scopedClass)} style={{ display: 'inline-flex', color, ...rs.style }}>
      {IconCmp ? <IconCmp size={size ?? 32} aria-hidden /> : null}
    </span>
  );
}

// --- Sosyal Bağlantılar ---

const SOCIAL_SIZE_PX: Record<SocialSize, number> = { small: 28, medium: 36, large: 44 };
const SOCIAL_LABEL: Record<string, string> = {
  x: 'X',
  facebook: 'f',
  instagram: 'IG',
  linkedin: 'in',
  youtube: 'YT',
  whatsapp: 'WA',
};

export function SocialLinksView({
  items,
  size,
  styleJson,
}: {
  items: SocialLinkItem[];
  size?: SocialSize;
  styleJson?: string;
}) {
  const rs = resolveBlockStyle(styleJson);
  useHeadStyle(rs.scopedClass, rs.css);
  const dim = SOCIAL_SIZE_PX[size ?? 'medium'];
  return (
    <div
      className={cx('cs-block cs-block--social', rs.scopedClass)}
      style={{ display: 'flex', gap: 8, flexWrap: 'wrap', ...rs.style }}
    >
      {items.map((item, i) => {
        const safe = safeUrlOrUndefined(item.url);
        const label = SOCIAL_LABEL[item.platform] ?? item.platform.slice(0, 2).toUpperCase();
        const chipStyle: CSSProperties = {
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: dim,
          height: dim,
          borderRadius: 999,
          background: 'var(--ds-surface-2, #eef2f7)',
          color: 'var(--ds-text-primary, #0f172a)',
          fontSize: Math.round(dim / 2.6),
          fontWeight: 700,
          textDecoration: 'none',
        };
        return safe ? (
          <a key={i} href={safe} aria-label={item.platform} title={item.platform} style={chipStyle}>
            {label}
          </a>
        ) : (
          <span key={i} aria-label={item.platform} title={item.platform} style={chipStyle}>
            {label}
          </span>
        );
      })}
    </div>
  );
}

// --- Ham HTML gömme (K-20 revizyonu: düzenlenebilir; render-zamanı sanitize) ---

export function HtmlEmbedView({ html, styleJson }: { html: string; styleJson?: string }) {
  const rs = resolveBlockStyle(styleJson);
  useHeadStyle(rs.scopedClass, rs.css);
  return (
    <div
      className={cx('cs-block cs-block--html-embed', rs.scopedClass)}
      style={Object.keys(rs.style).length > 0 ? rs.style : undefined}
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
    />
  );
}

// --- Referans (embed-marker) blokları: admin önizlemede hafif placeholder-kart ---

function RefPlaceholder({
  kind,
  detail,
  scopedClass,
  style,
}: {
  kind: string;
  detail: string;
  scopedClass?: string;
  style: CSSProperties;
}) {
  return (
    <div
      className={cx('cs-block cs-block--ref', scopedClass)}
      style={{
        padding: 20,
        borderRadius: 10,
        border: '1px dashed var(--ds-border, #cbd5e1)',
        background: 'var(--ds-surface-2, #f8fafc)',
        color: 'var(--ds-text-secondary, #64748b)',
        textAlign: 'center',
        ...style,
      }}
    >
      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>{kind}</div>
      <div style={{ fontWeight: 600, marginTop: 4, wordBreak: 'break-all' }}>{detail || '—'}</div>
    </div>
  );
}

export function FormRefView({ formId, styleJson }: { formId: string; styleJson?: string }) {
  const rs = resolveBlockStyle(styleJson);
  useHeadStyle(rs.scopedClass, rs.css);
  return <RefPlaceholder kind="Form" detail={formId} scopedClass={rs.scopedClass} style={rs.style} />;
}

export function SliderRefView({ sliderId, styleJson }: { sliderId: string; styleJson?: string }) {
  const rs = resolveBlockStyle(styleJson);
  useHeadStyle(rs.scopedClass, rs.css);
  return <RefPlaceholder kind="Slider" detail={sliderId} scopedClass={rs.scopedClass} style={rs.style} />;
}

export function MapRefView({
  lat,
  lng,
  zoom,
  styleJson,
}: {
  lat: number;
  lng: number;
  zoom: number;
  styleJson?: string;
}) {
  const rs = resolveBlockStyle(styleJson);
  useHeadStyle(rs.scopedClass, rs.css);
  const detail = `${lat}, ${lng} · z${clampMapZoom(zoom)}`;
  return <RefPlaceholder kind="Harita" detail={detail} scopedClass={rs.scopedClass} style={rs.style} />;
}
